import { default as React } from 'react';
import { useEntity, EntityRefLinks } from '@backstage/plugin-catalog-react';
import {
  RELATION_OWNED_BY,
  RELATION_PART_OF,
  getCompoundEntityRef,
} from '@backstage/catalog-model';
import { InfoCard, MarkdownContent } from '@backstage/core-components';
import { Grid, Typography, Chip } from '@material-ui/core';
import { useAboutCardStyles } from './aboutCardStyles';

const GENERAL_MANAGER_ANNOTATION = 'skystage.wellsky.com/general-manager';
const LEGACY_NAME_ANNOTATION = 'skystage.wellsky.com/legacy-name';

/**
 * Custom About card for System (platform) entities that includes
 * a General Manager field inline with Owner/Domain/Tag.
 */
export const SystemAboutCard = ({ variant }: { variant?: 'gridItem' }) => {
  const { entity } = useEntity();
  const classes = useAboutCardStyles();

  const description = entity.metadata?.description || 'No description';

  // Get owner relations
  const ownedByRelations: string[] = (entity.relations || [])
    .filter(r => r.type === RELATION_OWNED_BY)
    .map(r =>
      typeof r.targetRef === 'string'
        ? r.targetRef
        : String(getCompoundEntityRef(r.targetRef as any)),
    );

  // Get domain relations (partOf where target is a domain)
  const partOfDomainRelations: string[] = (entity.relations || [])
    .filter(
      r =>
        r.type === RELATION_PART_OF &&
        (typeof r.targetRef === 'string'
          ? r.targetRef.startsWith('domain:')
          : (r.targetRef as any)?.kind?.toLowerCase() === 'domain'),
    )
    .map(r =>
      typeof r.targetRef === 'string'
        ? r.targetRef
        : String(getCompoundEntityRef(r.targetRef as any)),
    );

  // Get General Manager from annotation
  const generalManagerAnnotation =
    entity.metadata?.annotations?.[GENERAL_MANAGER_ANNOTATION] || '';

  // Get Legacy Name from annotation
  const legacyName =
    entity.metadata?.annotations?.[LEGACY_NAME_ANNOTATION] || '';

  // If the annotation is in entity reference format (e.g., 'user:onspring/john-smith'),
  // use it directly. Otherwise, convert the plain name to entity reference format.
  const isEntityRef = generalManagerAnnotation.includes(':');

  // For entity references, use as-is. For plain names (backward compatibility),
  // convert to entity reference format automatically
  let generalManagerRef = '';
  if (isEntityRef) {
    generalManagerRef = generalManagerAnnotation;
  } else if (generalManagerAnnotation) {
    generalManagerRef = `user:onspring/${generalManagerAnnotation}`;
  }

  return (
    <InfoCard title="About" variant={variant}>
      <Grid container>
        {/* Description */}
        <Grid item xs={12}>
          <Typography variant="h2" className={classes.label}>
            Description
          </Typography>
          <div className={classes.description}>
            <MarkdownContent content={description} />
          </div>
        </Grid>

        {/* Owner */}
        {ownedByRelations.length > 0 && (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              Owner
            </Typography>
            <Typography variant="body2" className={classes.value}>
              <EntityRefLinks
                entityRefs={ownedByRelations}
                defaultKind="group"
              />
            </Typography>
          </Grid>
        )}

        {/* Domain */}
        {partOfDomainRelations.length > 0 ? (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              Domain
            </Typography>
            <Typography variant="body2" className={classes.value}>
              <EntityRefLinks
                entityRefs={partOfDomainRelations}
                defaultKind="domain"
              />
            </Typography>
          </Grid>
        ) : null}

        {/* General Manager */}
        {generalManagerRef && (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              General Manager
            </Typography>
            <Typography variant="body2" className={classes.value}>
              <EntityRefLinks
                entityRefs={[generalManagerRef]}
                defaultKind="user"
              />
            </Typography>
          </Grid>
        )}

        {/* Legacy Name */}
        {legacyName && (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              Legacy Name
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {legacyName}
            </Typography>
          </Grid>
        )}

        {/* Lifecycle */}
        {(entity.spec as Record<string, unknown>)?.lifecycle ? (
          <Grid item xs={12} sm={6} lg={4}>
            <Typography variant="h2" className={classes.label}>
              Lifecycle
            </Typography>
            <Typography variant="body2" className={classes.value}>
              {String((entity.spec as Record<string, unknown>).lifecycle)}
            </Typography>
          </Grid>
        ) : null}

        {/* Tag */}
        <Grid item xs={12} sm={6} lg={4}>
          <Typography variant="h2" className={classes.label}>
            Tag
          </Typography>
          <Chip
            label={entity.metadata.name}
            size="small"
            className={classes.chip}
          />
        </Grid>
      </Grid>
    </InfoCard>
  );
};
